import React from "react";
import {
  Box,
  Button,
  Icon,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "@salestrak/ui";
import { useAutomationPermissions } from "#app/hooks/automations/use-automation-permissions";
import { useAutomationRunsDialogActions } from "#app/lib/automation-runs-dialog-context";
import { useAutomationTouch } from "#app/lib/automation-touches";
import { useAutomationsEnabled } from "#app/lib/hooks/use-automations-enabled";

/**
 * The glyph a row wears for a few seconds after an automation moved or
 * updated it. A viewer who may read runs presses it to open the project's
 * automation activity; anyone else gets the fact in a tooltip. Renders
 * nothing while no touch is fresh, so hosts mount it unconditionally.
 */
export function AutomationTouchMarker({
  issueId,
  issueName,
}: {
  issueId: string;
  issueName?: string | null;
}) {
  const touch = useAutomationTouch(issueId);
  const enabled = useAutomationsEnabled();
  const { canViewRuns } = useAutomationPermissions();
  const { openDialog } = useAutomationRunsDialogActions();
  if (!touch) return null;

  const label = touch.kind === "moved" ? "Moved by automation" : "Updated by automation";
  const glyph = <Icon color="muted" name="zap" size="sm" />;

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          enabled && canViewRuns ? (
            <Button
              aria-label={label}
              size="icon-xs"
              variant="ghost"
              onClick={(event: React.MouseEvent) => {
                // The hosting row or card opens the project on click.
                event.stopPropagation();
                openDialog({ issueId, issueName }, "touch_marker");
              }}
            />
          ) : (
            <Box align="center" aria-label={label} direction="row" shrink={false} />
          )
        }
      >
        {glyph}
      </TooltipTrigger>
      <TooltipPopup side="top">{label}</TooltipPopup>
    </Tooltip>
  );
}
